// The console's calls. Every credential is a cookie the server set and page
// script cannot read: pdld-console-login while logging in, pdld-console once
// logged in. Nothing here stores or sees a secret.

async function post(path, body) {
    const response = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
    })
    let json = null
    if (response.ok) {
        try {
            json = await response.json()
        } catch (e) {
            json = null
        }
    }
    return { status: response.status, json }
}

// 200 with who is logged in and their clubs; 401 when nobody is (or the 8
// hours are up); 403 when they no longer administer any club.
export function readMe() {
    return post('/api/v1/console/me')
}

export function startLogin() {
    return post('/api/v1/console/login/start')
}

export function claimLogin(id) {
    return post('/api/v1/console/login/claim', { id })
}

export function logout() {
    return post('/api/v1/console/logout')
}

// Listens for the phone's answer. onState gets "pending", "scanned",
// "approved", "refused", "expired" or "claimed"; onLost is called when the
// server refused the stream outright (somebody else's request, or the cookie
// is gone) - EventSource reconnects on its own after a dropped connection,
// but not after a refusal. Returns the function that stops listening.
export function watchLogin(id, onState, onLost) {
    const source = new EventSource(`/api/v1/console/login/events?id=${encodeURIComponent(id)}`)

    source.addEventListener('state', event => {
        try {
            onState(JSON.parse(event.data).state)
        } catch (e) {
            // A malformed event is ignored; the next one carries the state.
        }
    })

    source.onerror = () => {
        if (source.readyState === EventSource.CLOSED) {
            onLost()
        }
    }

    return () => source.close()
}

// Courts. Every call names the club or the booking it is about; the server
// checks the session's admin still administers that club on every one, and
// answers 401 once the session is gone, 403 for a club - or a booking - that
// is not theirs, 400 for input it will not take and 409 when a slot changed
// under the request. Fields come back under their proto names, snake_case,
// defaults included.
export function readCourtDay(clubId, date) {
    return post('/api/v1/console/courts/day', { club_id: clubId, year: date.year, month: date.month, day: date.day })
}

export function readCourtMonth(clubId, month) {
    return post('/api/v1/console/courts/month', { club_id: clubId, year: month.year, month: month.month })
}

export function readCourtBooking(bookingId) {
    return post('/api/v1/console/courts/booking', { booking_id: bookingId })
}

// cells are { court_id, start_time }, start_time as the day grid sent it
// ("8:00 AM"). Slots somebody took in the meantime come back as skipped.
// showReason lets the club's members see the kind and label in the app.
// assigneeIds are the roster ids of the club members working on it.
export function blockCourtSlots(clubId, date, kind, label, cells, showReason, assigneeIds) {
    return post('/api/v1/console/courts/block', {
        club_id: clubId,
        kind,
        label,
        year: date.year,
        month: date.month,
        day: date.day,
        cells,
        show_reason: !!showReason,
        assignee_club_user_ids: assigneeIds || [],
    })
}

export function unblockCourt(bookingId, wholeGroup) {
    return post('/api/v1/console/courts/unblock', { booking_id: bookingId, whole_group: !!wholeGroup })
}

// Shows or hides a block's reason to the club's members; comes back with how
// many slots were changed.
export function updateCourtBlock(bookingId, wholeGroup, showReason) {
    return post('/api/v1/console/courts/block/update', {
        booking_id: bookingId,
        whole_group: !!wholeGroup,
        show_reason: !!showReason,
    })
}

// Creates a game on one free slot, with its players and organiser - see
// gameToCreate. 409 when the slot was taken meanwhile.
export function createCourtGame(clubId, date, cell, draft) {
    return post('/api/v1/console/courts/game', {
        club_id: clubId,
        court_id: cell.court_id,
        year: date.year,
        month: date.month,
        day: date.day,
        start_time: cell.start_time,
        ...draft,
    })
}

export function cancelCourtGame(bookingId) {
    return post('/api/v1/console/courts/cancel-game', { booking_id: bookingId })
}

// Opening hours, seven days Monday first, each { closed, open_minute,
// close_minute } in minutes since midnight. An update the club's future
// bookings would not fit is refused whole: saved comes back false, with the
// bookings in the way, and nothing is changed. 400 for hours it will not take.
export function readOpeningHours(clubId) {
    return post('/api/v1/console/hours/read', { club_id: clubId })
}

export function updateOpeningHours(clubId, days) {
    return post('/api/v1/console/hours/update', { club_id: clubId, days })
}

// Members: the club's roster, with the players' requests to join it first
// (is_pending). Same answers as above: 401 once the session is gone, 403 for
// a club or a member that is not theirs, 400 for input the server will not
// take - a birth date that is not a real past day, a role on a request, an
// admin removing themselves - and 409 for a member already on the roster.
export function listMembers(clubId, search) {
    return post('/api/v1/console/members/list', { club_id: clubId, search: search || '' })
}

export function createMember(clubuser) {
    return post('/api/v1/console/members/create', { clubuser })
}

// clubuser carries every editable field: the server sets them all.
export function updateMember(clubuser) {
    return post('/api/v1/console/members/update', { clubuser })
}

// Removes a member, or refuses a request to join.
export function deleteMember(id) {
    return post('/api/v1/console/members/delete', { id })
}

// Accepts a request to join.
export function validateMember(id) {
    return post('/api/v1/console/members/validate', { id })
}

// Padelr players matching a search, to add one to the roster linked to their
// account. Each says who they are and whether they are already on the roster
// (is_member) - never their email or phone.
export function searchPlayers(clubId, search) {
    return post('/api/v1/console/members/players', { club_id: clubId, search: search || '' })
}

// The club members working on a block (azure-zephyr-2), by roster id. The
// whole list each time: it replaces the one there, and an empty one removes
// everybody. 400 for somebody who is not a member of the club.
export function setCourtBlockAssignees(bookingId, wholeGroup, clubUserIds) {
    return post('/api/v1/console/courts/block/assignees', {
        booking_id: bookingId,
        whole_group: !!wholeGroup,
        assignee_club_user_ids: clubUserIds,
    })
}

// Who held the club's courts from one day to another, both included, and for
// what. from and to are { year, month, day }; at most 366 days.
export function readCourtOccupancy(clubId, from, to) {
    const iso = d => `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`
    return post('/api/v1/console/courts/occupancy', { club_id: clubId, from: iso(from), to: iso(to) })
}
